import { useEffect, useRef, useState } from 'react';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow.right.svg';
import type { DragEvent } from '../../types/OfflineLocation';

// 백분율 값(예: "20%")을 실제 rem 값으로 변환하는 높이 변환 함수
const getRemFromPercentage = (percentage: string): number => {
  if (typeof window === 'undefined') return 0;

  // 백분율에서 숫자만 추출
  const percentValue = Number.parseFloat(percentage.replace('%', ''));

  // window 높이를 가져와서 백분율 적용 (픽셀 단위)
  const pixelHeight = (window.innerHeight * percentValue) / 100;

  // 픽셀을 rem으로 변환
  const remSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return pixelHeight / remSize;
};

// rem 값을 백분율로 변환
const getPercentageFromRem = (remValue: number): string => {
  if (typeof window === 'undefined') return '0%';

  // rem을 픽셀로 변환
  const remSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  const pixelValue = remValue * remSize;

  // 픽셀을 백분율로 변환
  return `${(pixelValue / window.innerHeight) * 100}%`;
};

// px 값을 백분율로 변환
export function getPercentageFromPx(pxValue: number): string {
  if (typeof window === 'undefined') return '0%';

  // 현재 윈도우 높이 가져오기
  const windowHeight = window.innerHeight;

  // 퍼센트 계산 (px값 / 윈도우 높이 * 100)
  const percentValue = (pxValue / windowHeight) * 100;

  // 소수점 2자리까지 반올림하고 % 문자열 추가
  return `${percentValue.toFixed(2)}%`;
}

interface IOfflineLocationBottomSheetProps {
  selectedTab?: number;
  selectedPinOrOption?: string;
  headerTitleElement: React.ReactNode;
  children?: React.ReactNode; // children prop added
  minHeight?: string; // minHeight prop added
  midHeight?: string; // midHeight prop added
  fullHeight?: string; // fullHeight prop added
  positionsLength?: number; // positionsLength prop added
}

// 내부 상태 변경을 외부(부모)로 알리기 위한 최소 이벤트 헬퍼
const dispatchBottomSheetHeight = (percent: string) => {
  if (typeof window === 'undefined') return;
  const value = Number.parseFloat(percent.replace('%', ''));
  const heightPx = (window.innerHeight * value) / 100;
  window.dispatchEvent(new CustomEvent('offlineBottomSheet:height', { detail: { heightPx } }));
};

const dispatchBottomSheetDragging = (isDragging: boolean) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('offlineBottomSheet:dragging', { detail: { isDragging } }));
};

const dispatchBottomSheetOffsetBottom = (sheetEl: HTMLDivElement | null) => {
  if (typeof window === 'undefined' || !sheetEl) return;
  const rect = sheetEl.getBoundingClientRect();
  const offsetBottom = window.innerHeight - rect.top;
  window.dispatchEvent(
    new CustomEvent('offlineBottomSheet:offset-bottom', { detail: { offsetBottom } })
  );
};

const dispatchBottomSheetPosition = (position: 'minimized' | 'mid' | 'full' | 'dragging') => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('offlineBottomSheet:position', { detail: { position } }));
};

const OfflineLocationBottomSheet = ({
  selectedTab = 0,
  selectedPinOrOption,
  headerTitleElement,
  children = <></>,
  minHeight = '10%',
  midHeight = '40%', // 중간 높이 추가
  fullHeight = '90%'
}: IOfflineLocationBottomSheetProps) => {
  // 상태 관리
  const [isFullHeightBottomSheet, setIsFullHeightBottomSheet] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startY, setStartY] = useState<number>(0);
  const [currentHeight, setCurrentHeight] = useState<string>(
    selectedPinOrOption === '' ? minHeight : midHeight
  );
  const [position, setPosition] = useState<'minimized' | 'mid' | 'full' | 'dragging'>('minimized');
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragAreaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null); // 컨텐츠 영역에 대한 ref
  // 각 단계별 rem 높이
  const minHeightRem = getRemFromPercentage(minHeight);
  const midHeightRem = getRemFromPercentage(midHeight);
  const fullHeightRem = getRemFromPercentage(fullHeight);

  const minimizedBottomSheet = () => {
    setIsFullHeightBottomSheet(false);
    setCurrentHeight(minHeight);
    setPosition('minimized');
    dispatchBottomSheetPosition('minimized');
    dispatchBottomSheetOffsetBottom(sheetRef.current);
    dispatchBottomSheetHeight(minHeight);
    dispatchBottomSheetDragging(false);
  };

  // 드래그 시작 처리
  const handleDragStart = (e: DragEvent): void => {
    const clientY = e.type.includes('touch')
      ? (e as React.TouchEvent<HTMLDivElement>).touches[0].clientY
      : (e as React.MouseEvent<HTMLDivElement>).clientY;
    setIsDragging(true);
    dispatchBottomSheetPosition('dragging');
    dispatchBottomSheetDragging(true);
    setStartY(clientY);
    setPosition('dragging');
  };

  // 드래그 이동 처리
  const handleDragMove = (e: MouseEvent | TouchEvent): void => {
    if (!isDragging) return;

    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaY = startY - clientY;

    // rem 단위로 deltaY 변환
    const remSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    const deltaRem = deltaY / remSize;

    // 기존 시트 높이를 rem으로 계산
    const currentHeightRem = getRemFromPercentage(currentHeight);

    // 새 높이 계산 (위로 드래그하면 커지고, 아래로 드래그하면 작아짐)
    let newHeightRem = currentHeightRem + deltaRem;

    // 높이 제한
    newHeightRem = Math.max(minHeightRem, Math.min(newHeightRem, fullHeightRem));

    const heightRatio = getPercentageFromRem(newHeightRem);
    setIsFullHeightBottomSheet(heightRatio === '100%');
    setCurrentHeight(heightRatio);
    dispatchBottomSheetOffsetBottom(sheetRef.current);
    dispatchBottomSheetHeight(heightRatio);

    // 드래그 시작점 업데이트 (연속적인 드래그를 위해)
    setStartY(clientY);
  };

  // 드래그 종료 처리 - 3단계 스냅 로직으로 수정
  const handleDragEnd = (): void => {
    if (!isDragging) return;
    setIsDragging(false);
    dispatchBottomSheetDragging(false);

    // 현재 높이를 픽셀로 변환
    const currentHeightRem = getRemFromPercentage(currentHeight);

    // 각 상태와의 거리 계산
    const distToMin = Math.abs(currentHeightRem - minHeightRem);
    const distToMid = Math.abs(currentHeightRem - midHeightRem);
    const distToFull = Math.abs(currentHeightRem - fullHeightRem);

    // 가장 가까운 상태로 스냅
    if (distToMin <= distToMid && distToMin <= distToFull) {
      // 최소 높이가 가장 가까움
      setCurrentHeight(minHeight);
      setPosition('minimized');
      dispatchBottomSheetPosition('minimized');
      dispatchBottomSheetOffsetBottom(sheetRef.current);
      dispatchBottomSheetHeight(minHeight);

      // 최소 높이로 갈 때 스크롤 초기화
      if (contentRef.current) {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          }
        }, 100); // 트랜지션 시작 후 스크롤 초기화
      }

      // 바디 스크롤 다시 활성화
      document.body.style.overflow = '';
    } else if (distToMid <= distToMin && distToMid <= distToFull) {
      // 중간 높이가 가장 가까움
      setCurrentHeight(midHeight);
      setPosition('mid');
      dispatchBottomSheetPosition('mid');
      dispatchBottomSheetOffsetBottom(sheetRef.current);
      dispatchBottomSheetHeight(midHeight);

      // 바디 스크롤 비활성화
      document.body.style.overflow = 'hidden';
    } else {
      // 최대 높이가 가장 가까움
      setCurrentHeight(fullHeight);
      setPosition('full');
      dispatchBottomSheetPosition('full');
      dispatchBottomSheetOffsetBottom(sheetRef.current);
      dispatchBottomSheetHeight(fullHeight);

      // 바디 스크롤 비활성화
      document.body.style.overflow = 'hidden';
    }
  };

  // 외부 클릭 처리 - mid와 full 상태 모두에서 동작하도록 수정
  const handleOutsideClick = (e: MouseEvent | TouchEvent): void => {
    // Bottom Sheet가 중간이나 완전 열린 상태이고, 클릭이 시트 외부에서 발생한 경우
    if (
      (position === 'full' || position === 'mid') &&
      sheetRef.current &&
      !sheetRef.current.contains(e.target as Node)
    ) {
      // 완전 열림 상태였다면 중간으로, 중간 상태였다면 최소로 이동
      if (position === 'full') {
        setCurrentHeight(midHeight);
        setPosition('mid');
        dispatchBottomSheetPosition('mid');
        dispatchBottomSheetOffsetBottom(sheetRef.current);
        dispatchBottomSheetHeight(midHeight);

        // mid 상태에서도 배경 스크롤 비활성화 유지
        document.body.style.overflow = 'hidden';
      } else {
        setCurrentHeight(minHeight);
        setPosition('minimized');
        dispatchBottomSheetPosition('minimized');
        dispatchBottomSheetOffsetBottom(sheetRef.current);
        dispatchBottomSheetHeight(minHeight);

        // 최소 상태에서는 배경 스크롤 활성화
        document.body.style.overflow = '';

        // 최소 높이로 갈 때 스크롤 초기화
        if (contentRef.current) {
          setTimeout(() => {
            if (contentRef.current) {
              contentRef.current.scrollTop = 0;
            }
          }, 100); // 트랜지션 시작 후 스크롤 초기화
        }
      }
    }
  };

  // 이벤트 리스너 등록
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // 드래그 관련 이벤트
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchend', handleDragEnd);
    document.addEventListener('mousemove', handleDragMove as unknown as EventListener);
    document.addEventListener('touchmove', handleDragMove as unknown as EventListener, {
      passive: false
    });

    // 외부 클릭은 mousedown 대신 mouseup으로 변경 (드래그와 구분하기 위해)
    document.addEventListener('mouseup', handleOutsideClick as unknown as EventListener);
    document.addEventListener('touchend', handleOutsideClick as unknown as EventListener);

    return () => {
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchend', handleDragEnd);
      document.removeEventListener('mousemove', handleDragMove as unknown as EventListener);
      document.removeEventListener('touchmove', handleDragMove as unknown as EventListener);
      document.removeEventListener('mouseup', handleOutsideClick as unknown as EventListener);
      document.removeEventListener('touchend', handleOutsideClick as unknown as EventListener);
    };
  }, [isDragging, startY, position, currentHeight]);

  // 스크롤 제어 관련 효과
  useEffect(() => {
    // position이 바뀔 때마다 스크롤 제어 상태 업데이트
    if (position === 'minimized') {
      // 최소화 상태에서는 배경 스크롤 허용
      document.body.style.overflow = '';

      // 최소화 상태로 변경됐을 때 스크롤 위치 초기화
      if (contentRef.current) {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          }
        }, 100); // 트랜지션이 시작된 후 스크롤 초기화
      }
    } else {
      // 'mid', 'full', 'dragging' 상태에서는 배경 스크롤 비활성화
      document.body.style.overflow = 'hidden';
    }

    // 컴포넌트 언마운트 시 스크롤 제어 초기화
    return () => {
      document.body.style.overflow = '';
    };
  }, [position]);

  useEffect(() => {
    if (selectedTab === 0) {
      setCurrentHeight(minHeight);
      setPosition('minimized');
    } else if (selectedTab === 1) {
      setCurrentHeight(fullHeight);
      setPosition('full');
    }
  }, [selectedTab]);

  // map pin click 시 중간 높이로 올라감
  useEffect(() => {
    if (selectedPinOrOption !== '') {
      setCurrentHeight(midHeight);
      setPosition('mid');
    }
  }, [selectedPinOrOption]);

  // 스크롤과 드래그 방지
  useEffect(() => {
    const preventDefault = (e: TouchEvent): void => {
      // minimized 상태가 아닐 때는 BottomSheet 밖의 스크롤 모두 방지
      if (position !== 'minimized') {
        // BottomSheet 내부 컨텐츠 영역을 스크롤 중인 경우만 예외 처리
        const isInsideContent = contentRef.current && contentRef.current.contains(e.target as Node);

        if (!isInsideContent) {
          e.preventDefault();
        }
      } else if (isDragging) {
        // minimized 상태이지만 드래그 중인 경우에는 preventDefault
        e.preventDefault();
      }
    };

    // 터치 이벤트 방지
    document.addEventListener('touchmove', preventDefault as unknown as EventListener, {
      passive: false
    });

    return () => {
      // 이벤트 리스너 제거
      document.removeEventListener('touchmove', preventDefault as unknown as EventListener);
    };
  }, [isDragging, position]);

  return (
    <div
      className="fixed inset-0 z-10 pointer-events-none flex justify-center"
      // minimized가 아닌 경우 배경 클릭 이벤트 활성화
      style={{
        pointerEvents: position !== 'minimized' ? 'auto' : 'none'
      }}
    >
      {/* 반투명 오버레이 - minimized 상태가 아닐 때만 표시 */}
      {position !== 'minimized' && (
        <div
          className="absolute inset-0 bg-black bg-opacity-5"
          style={{
            touchAction: 'none',
            pointerEvents: 'auto'
          }}
        />
      )}
      <div
        ref={sheetRef}
        className="absolute bottom-0 safeAreaMarginBottom bg-background-elevated rounded-t-[20px] shadow-lg pointer-events-auto w-full max-w-[36rem]"
        style={{
          height: currentHeight,
          transition: isDragging ? 'none' : 'height 0.3s ease-out',
          boxShadow: '0px 0px 25px 0px rgba(0, 0, 0, 0.36)',
          // 드래그 중일 때 스크롤 방지
          touchAction: isDragging ? 'none' : 'auto'
        }}
      >
        {/* 드래그 핸들 */}
        <div
          ref={dragAreaRef}
          className="w-full h-[20px] flex justify-center items-center cursor-grab active:cursor-grabbing"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="w-[36px] h-1 bg-sub-light-3 dark:bg-sub-dark-3 rounded-full mb-1" />
        </div>

        {/* 헤더 영역 */}
        <div
          className="px-6 py-[6px] text-main-light-2 dark:text-main-dark-2 text-[17px] font-semibold"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="flex flex-row justify-between items-center">
            {headerTitleElement}
            {isFullHeightBottomSheet && (
              <div
                className="w-[28px] h-[28px] rounded-full bg-surface-lightLv2 dark:bg-surface-darkLv2 flex items-center justify-center"
                onClick={() => minimizedBottomSheet()}
              >
                <ArrowRightIcon className="w-[16px] h-[16px] transform rotate-90 fill-main-light-2 dark:fill-main-dark-2" />
              </div>
            )}
          </div>
        </div>

        {/* 컨텐츠 영역 */}
        <div
          ref={contentRef}
          className="flex flex-col overflow-y-auto pt-[9px] pb-[72px] "
          style={{
            maxHeight: 'calc(100% - 56px)', // 헤더와 드래그 핸들 높이를 제외한 나머지 영역
            height: '100%',
            // 'minimized'가 아닌 상태에서만 스크롤 허용
            overflowY: position !== 'minimized' ? 'auto' : 'hidden'
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default OfflineLocationBottomSheet;
